import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { API } from "../../../api";
import { Job, Robot, Shift } from "../../../models";
import { UserContext } from "../../../hooks/UserContext";
import { FileDownloader } from "../../../utilities/downloadFile";
import { IAirtableAttachment } from "../../../utilities/types";
import { GeoJSON } from "../../../utilities/utils";
import type { Mission, IJob, IDeal, ICompany, MissionCSV } from "@rogoag/shared-ts";

export const SAMPLE_TYPE_LABELS: Record<number, string> = {
	0: "Regular",
	1: "ISNT 1",
	2: "ISNT 2",
	3: "Cyst",
};

export function formatTimestamp(ts: string | number | undefined): string {
	if (!ts) return "";
	let ms: number;
	if (typeof ts === "string") {
		ms = new Date(ts).getTime();
	} else if (ts > 1e15) {
		ms = ts / 1000;
	} else if (ts > 1e12) {
		ms = ts;
	} else {
		ms = ts * 1000;
	}
	if (ms > 1e15) ms = ms / 1000;
	const date = new Date(ms);
	if (isNaN(date.getTime()) || date.getTime() === 0) return "";
	return date.toLocaleString();
}

function triggerCsvDownload(headers: string[], rows: string[][], filename: string) {
	const escape = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
	const csv = [headers.map(escape).join(","), ...rows.map((row) => row.map(escape).join(","))].join("\n");
	const blob = new Blob([csv], { type: "text/csv" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	a.click();
	URL.revokeObjectURL(url);
}

const ATTACHMENT_LABELS: Record<string, string> = {
	pointsSHP: "Points SHP",
	boundarySHP: "Boundary SHP",
	boundaryGeoJSON: "Boundary GeoJSON",
	boundaryGeoJson: "Boundary GeoJSON",
	zonesSHP: "Zones SHP",
	executedPointsSHP: "Executed Points SHP",
	executedBoundarySHP: "Executed Boundary SHP",
	executedPointsBoundarySHP: "Executed Pts+Bnd SHP",
	labPDF: "Lab PDF",
	labCSV: "Lab CSV",
	labResults: "Lab Results",
	fieldNotes: "Field Notes",
	exeMission: "Executed Mission",
	intMission: "Intended Mission",
};

export interface AvailableAttachment {
	key: string;
	label: string;
	attachments: IAirtableAttachment[];
}

export const usePostSamplingIssuesViewModel = () => {
	const currentUser = useContext(UserContext);
	const isDeveloper = currentUser?.isDeveloper ?? false;

	const [jobs, setJobs] = useState<Job[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [fixingIds, setFixingIds] = useState<Set<string>>(new Set());
	const [fixingAll, setFixingAll] = useState(false);
	const [countdown, setCountdown] = useState<number | null>(null);
	const countdownRef = useRef<ReturnType<typeof setInterval>>(undefined);

	const [selectedJob, setSelectedJob] = useState<Job | null>(null);
	const [mapLoading, setMapLoading] = useState(false);
	const [exeMission, setExeMission] = useState<Mission | null>(null);
	const [intMission, setIntMission] = useState<Mission | null>(null);
	const [missionsLoading, setMissionsLoading] = useState(false);
	const [csvDryRunData, setCsvDryRunData] = useState<MissionCSV[] | null>(null);
	const [csvDryRunModalOpen, setCsvDryRunModalOpen] = useState(false);
	const [fudgingCoreId, setFudgingCoreId] = useState<string | null>(null);
	const [shift, setShift] = useState<Shift | null>(null);
	const [robot, setRobot] = useState<Robot | null>(null);

	const [mapOpen, setMapOpen] = useState(false);
	const [detailModal, setDetailModal] = useState<"samples" | "cores" | null>(null);
	const [downloadAnchor, setDownloadAnchor] = useState<HTMLElement | null>(null);

	const groupedJobs = useMemo(
		() =>
			Object.entries(
				jobs.reduce<Record<string, Job[]>>((groups, job) => {
					const company = job.companyName || "Unknown Company";
					(groups[company] ??= []).push(job);
					return groups;
				}, {}),
			).sort(([a], [b]) => a.localeCompare(b)),
		[jobs],
	);

	useEffect(() => {
		return () => {
			if (countdownRef.current) {
				clearInterval(countdownRef.current);
			}
		};
	}, []);

	const sleepWithCountdown = useCallback((seconds: number) => {
		return new Promise<void>((resolve) => {
			if (countdownRef.current) {
				clearInterval(countdownRef.current);
			}
			setCountdown(seconds);
			countdownRef.current = setInterval(() => {
				setCountdown((prev) => {
					if (prev !== null && prev <= 1) {
						clearInterval(countdownRef.current);
						resolve();
						return null;
					}
					return prev !== null ? prev - 1 : null;
				});
			}, 1000);
		});
	}, []);

	useEffect(() => {
		if (isDeveloper) {
			fetchJobs();
		}
	}, [isDeveloper]);

	const fetchJobs = async () => {
		setLoading(true);
		setError(null);
		try {
			const result = await API.Data.Jobs.issues.getPostSamplingStuckJobs();
			setJobs(result);
		} catch (err: any) {
			setError(`Failed to load post-sampling issues: ${err.message || "Unknown error"}`);
		} finally {
			setLoading(false);
		}
	};

	const selectJob = useCallback(async (job: Job) => {
		setSelectedJob(job);
		setExeMission(null);
		setIntMission(null);
		setCsvDryRunData(null);
		setShift(null);
		setRobot(null);
		if (job.shiftId) {
			API.Data.Shifts.get
				.byId(job.shiftId)
				.then((s) => {
					setShift(s);
					if (s.robotId) {
						const fetchRobot =
							s.startDate && s.endDate
								? API.Data.Robots.robots.byIdWithRaw(s.robotId, s.startDate, s.endDate)
								: API.Data.Robots.get.byId(s.robotId);
						fetchRobot.then((r) => setRobot(r)).catch(() => {});
					}
				})
				.catch(() => {});
		}

		// Load boundary, points, and zones GeoJSON for JobMap
		setMapLoading(true);
		try {
			const [boundary, points, zones] = await Promise.all([
				GeoJSON.getBoundary(job),
				GeoJSON.getPoints(job),
				GeoJSON.getZones(job),
			]);

			if (boundary || points || zones) {
				const updatedJob = {
					...job,
					files: {
						...job.files,
						boundaryGeoJSON: boundary ?? undefined,
						pointsGeoJSON: points ?? undefined,
						zonesGeoJSON: zones ?? undefined,
					},
				};
				setSelectedJob(updatedJob);
			}
		} catch (err) {
			console.warn("Failed to load GeoJSON for map:", err);
		} finally {
			setMapLoading(false);
		}

		// Load missions in parallel
		setMissionsLoading(true);
		try {
			const [exe, int] = await Promise.all([job.exeMission(), job.intMission()]);
			setExeMission(exe ?? null);
			setIntMission(int ?? null);

			// Generate CSV dry-run data if executed mission is available
			if (exe) {
				try {
					const deals = job.dealIds.length > 0 ? await API.Data.Deals.get.byIds(job.dealIds) : [];
					const deal = deals[0];
					const companyId = deal?.companyId;
					const companies = companyId ? await API.Data.Companies.get.byIds([companyId]) : [];
					const company = companies[0];

					const iJob: IJob = {
						id: job.id,
						name: job.fieldName,
						sampleDateManOverrideDate: job.dateSampledManualOverride
							? new Date(job.dateSampledManualOverride).toISOString()
							: undefined,
						jobFlags: job.jobFlags,
						eventId: job.eventId,
						grower: [job.growerName],
						finalLabAccNumber: job.labAccountNumber ? [job.labAccountNumber] : undefined,
						labInstructions: job.labInstructions,
					};

					const iDeal: IDeal = deal
						? { id: deal.id, fieldParameters: deal.fieldParameters, samplingTypeSpecial: deal.samplingTypeSpecial }
						: { id: "" };

					const iCompany: ICompany = {
						id: company?.id ?? "",
						externalSampleReferenceIdColumn: company?.externalSampleReferenceIdColumn,
					};

					setCsvDryRunData(exe.generateLabCSVs(iJob, iDeal, iCompany));
				} catch (err) {
					console.warn("Failed to generate CSV dry-run:", err);
				}
			}
		} catch (err) {
			console.warn("Failed to load missions:", err);
		} finally {
			setMissionsLoading(false);
		}
	}, []);

	const getAvailableAttachments = useCallback((job: Job): AvailableAttachment[] => {
		if (!job.attachments) return [];
		const result: AvailableAttachment[] = [];
		for (const [key, label] of Object.entries(ATTACHMENT_LABELS)) {
			const attachments = job.attachments[key as keyof typeof job.attachments] as IAirtableAttachment[] | undefined;
			if (attachments && attachments.length > 0) {
				result.push({ key, label, attachments });
			}
		}
		return result;
	}, []);

	const downloadAttachment = useCallback(async (attachment: AvailableAttachment, jobId: string) => {
		if (attachment.attachments.length === 1) {
			await FileDownloader.downloadAttachment(
				attachment.attachments[0],
				attachment.attachments[0].filename,
				false,
				jobId,
			);
		} else {
			await FileDownloader.downloadAttachments(
				attachment.attachments,
				attachment.attachments[0].filename,
				true,
				false,
				jobId,
			);
		}
	}, []);

	const fixOne = async (id: string) => {
		setFixingIds((prev) => new Set(prev).add(id));
		setError(null);
		try {
			await API.Data.Jobs.issues.fixPostSamplingStuckJobs([id]);
			await sleepWithCountdown(15);
			await fetchJobs();
		} catch (err: any) {
			setError(`Failed to fix job: ${err.message || "Unknown error"}`);
		} finally {
			setFixingIds((prev) => {
				const next = new Set(prev);
				next.delete(id);
				return next;
			});
		}
	};

	const downloadCsvDryRun = useCallback(() => {
		if (!csvDryRunData) return;
		for (const csv of csvDryRunData) {
			const blob = new Blob([csv.toString()], { type: "text/csv" });
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = csv.filename;
			a.click();
			URL.revokeObjectURL(url);
		}
	}, [csvDryRunData]);

	const fudgeCore = async (jobId: string, coreId: string) => {
		setFudgingCoreId(coreId);
		setError(null);
		try {
			await API.Missions.fudgeCore(jobId, coreId);
			await API.Data.Jobs.issues.fixPostSamplingStuckJobs([jobId]);
			await new Promise((r) => setTimeout(r, 10_000));
			await fetchJobs();
			if (selectedJob) {
				await selectJob(selectedJob);
			}
		} catch (err: any) {
			setError(`Failed to fudge core: ${err.message || "Unknown error"}`);
		} finally {
			setFudgingCoreId(null);
		}
	};

	const deleteCores = async (jobId: string, coreIds: string[]) => {
		setFudgingCoreId("bulk");
		setError(null);
		try {
			await API.Missions.deleteCores(jobId, coreIds);
			await API.Data.Jobs.issues.fixPostSamplingStuckJobs([jobId]);
			await new Promise((r) => setTimeout(r, 15_000));
			await fetchJobs();
			if (selectedJob) {
				await selectJob(selectedJob);
			}
		} catch (err: any) {
			setError(`Failed to delete cores: ${err.message || "Unknown error"}`);
		} finally {
			setFudgingCoreId(null);
		}
	};

	const setAverageCoreLength = async (jobId: string, coreIds: string[]) => {
		setFudgingCoreId("bulk");
		setError(null);
		try {
			await API.Missions.setAverageCoreLength(jobId, coreIds);
			await API.Data.Jobs.issues.fixPostSamplingStuckJobs([jobId]);
			await new Promise((r) => setTimeout(r, 10_000));
			await fetchJobs();
			if (selectedJob) {
				await selectJob(selectedJob);
			}
		} catch (err: any) {
			setError(`Failed to set average core length: ${err.message || "Unknown error"}`);
		} finally {
			setFudgingCoreId(null);
		}
	};

	const fixAll = async () => {
		const ids = jobs.map((j) => j.id);
		if (ids.length === 0) return;

		setFixingAll(true);
		setFixingIds(new Set(ids));
		setError(null);
		try {
			await API.Data.Jobs.issues.fixPostSamplingStuckJobs(ids);
			await sleepWithCountdown(120);
			await fetchJobs();
		} catch (err: any) {
			setError(`Failed to fix all jobs: ${err.message || "Unknown error"}`);
		} finally {
			setFixingAll(false);
			setFixingIds(new Set());
		}
	};

	const downloadSamplesCsv = useCallback(() => {
		if (!exeMission) return;
		const samples = exeMission.centroids;
		const headers = [
			"ID",
			"Sample ID",
			"Original Sample ID",
			"Bag ID",
			"Order",
			"Type",
			"Start Depth",
			"End Depth",
			"Core Length",
			"# Cores",
			"Lat",
			"Lng",
			"Sampled At",
			"Scanned At",
			"Pulled At",
			"Box UID",
			"External Ref ID",
			"Change",
			"Operator",
		];
		const rows = samples.map((s) => [
			s.id,
			String(s.extendedData.sample_id || ""),
			String(s.extendedData.original_sample_id || ""),
			String(s.extendedData.bag_id || ""),
			String(s.extendedData.order || ""),
			String(SAMPLE_TYPE_LABELS[s.extendedData.sample_type as number] ?? s.extendedData.sample_type ?? ""),
			String(s.extendedData.start_depth || ""),
			String(s.extendedData.end_depth || ""),
			String(s.extendedData.core_length_cm || ""),
			String(s.cores?.length ?? 0),
			s.coordinates?.[1]?.toFixed(6) ?? "",
			s.coordinates?.[0]?.toFixed(6) ?? "",
			formatTimestamp(s.extendedData.sampled_at),
			formatTimestamp(s.extendedData.scanned_at),
			formatTimestamp(s.extendedData.pulled_at),
			String(s.extendedData.box_uid || ""),
			String(s.extendedData.external_reference_id || ""),
			s.extendedData.change_type && s.extendedData.change_type !== "None"
				? `${s.extendedData.change_type}${s.extendedData.change_reason ? `: ${s.extendedData.change_reason}` : ""}`
				: "",
			String(s.extendedData.operator || ""),
		]);
		triggerCsvDownload(headers, rows, "samples.csv");
	}, [exeMission]);

	const downloadCoresCsv = useCallback(() => {
		if (!exeMission) return;
		const cores = exeMission.cores;
		const headers = [
			"ID",
			"Sample ID",
			"Original Sample ID",
			"Bag ID",
			"Order",
			"Type",
			"Start Depth",
			"End Depth",
			"Core Length",
			"Planned Lat",
			"Planned Lon",
			"Pulled Lat",
			"Pulled Lon",
			"Sampled At",
			"Scanned At",
			"Pulled At",
			"Box UID",
			"External Ref ID",
			"Change Type",
		];
		const rows = cores.map((c) => [
			c.id,
			String(c.extendedData.sample_id || ""),
			String(c.extendedData.original_sample_id || ""),
			String(c.extendedData.bag_id || ""),
			String(c.extendedData.order || ""),
			String(SAMPLE_TYPE_LABELS[c.extendedData.sample_type as number] ?? c.extendedData.sample_type ?? ""),
			String(c.extendedData.start_depth || ""),
			String(c.extendedData.end_depth || ""),
			String(c.extendedData.core_length_cm || ""),
			c.plannedLatitude?.toFixed(6) ?? "0",
			c.plannedLongitude?.toFixed(6) ?? "0",
			c.pulledLatitude?.toFixed(6) ?? "0",
			c.pulledLongitude?.toFixed(6) ?? "0",
			formatTimestamp(c.extendedData.sampled_at),
			formatTimestamp(c.extendedData.scanned_at),
			formatTimestamp(c.extendedData.pulled_at),
			String(c.extendedData.box_uid || ""),
			String(c.extendedData.external_reference_id || ""),
			String(c.extendedData.change_type || ""),
		]);
		triggerCsvDownload(headers, rows, "cores.csv");
	}, [exeMission]);

	return {
		isDeveloper,
		jobs,
		loading,
		error,
		fixingIds,
		fixingAll,
		fixOne,
		fixAll,
		countdown,
		selectedJob,
		selectJob,
		mapLoading,
		exeMission,
		intMission,
		missionsLoading,
		getAvailableAttachments,
		downloadAttachment,
		csvDryRunData,
		csvDryRunModalOpen,
		setCsvDryRunModalOpen,
		downloadCsvDryRun,
		downloadSamplesCsv,
		downloadCoresCsv,
		fudgeCore,
		deleteCores,
		setAverageCoreLength,
		fudgingCoreId,
		shift,
		robot,
		mapOpen,
		setMapOpen,
		detailModal,
		setDetailModal,
		downloadAnchor,
		setDownloadAnchor,
		groupedJobs,
	};
};
